import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Share2, Download, Copy, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "./mkMedia";

const fileName = (item) => `PTG-${(item.title || "post").replace(/[^a-z0-9]+/gi, "-").slice(0, 40)}.${item.kind === "video" ? "mp4" : "jpg"}`;

async function getFile(item) {
  const res = await fetch(mediaUrl(item.path));
  if (!res.ok) throw new Error("download failed");
  const blob = await res.blob();
  return new File([blob], fileName(item), { type: item.kind === "video" ? "video/mp4" : "image/jpeg" });
}

export const StatusShare = ({ item, testPrefix }) => {
  const [busy, setBusy] = useState("");
  const cached = React.useRef(null);
  const loadFile = async () => { if (!cached.current) cached.current = await getFile(item); return cached.current; };
  const copy = () => navigator.clipboard.writeText(item.caption || "").then(() => toast.success("Caption copied — paste it in WhatsApp")).catch(() => toast.error("Copy blocked — long-press the caption to copy"));
  const download = async (quiet) => {
    setBusy("dl");
    try {
      const f = await loadFile();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(f); a.download = f.name; document.body.appendChild(a); a.click(); a.remove();
      if (!quiet) toast.success("Saved — open WhatsApp → Updates → My status to add it");
    } catch { window.open(mediaUrl(item.path, true), "_blank"); }
    finally { setBusy(""); }
  };
  const share = async () => {
    setBusy("share");
    const wasCached = !!cached.current;
    try {
      const f = await loadFile();
      if (navigator.canShare && navigator.canShare({ files: [f] })) {
        try {
          await navigator.share({ files: [f], text: item.caption || "" });
          toast.message("Choose WhatsApp → My status. Use Copy caption to add the text.");
        } catch (e) {
          if (e?.name === "AbortError") return;
          if (e?.name === "NotAllowedError" && !wasCached) { toast.message("Ready — tap Share to WhatsApp Status again"); return; }
          throw e;
        }
      } else {
        await download(true);
        toast.message("Direct sharing isn't supported here. Saved the file — add it in WhatsApp → Updates → My status.");
      }
    } catch { toast.error("Sharing failed — use Download instead"); }
    finally { setBusy(""); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-share`}>
      <Button size="sm" onClick={share} disabled={!!busy} className="h-8 bg-green-600 hover:bg-green-700 text-white" data-testid={`${testPrefix}-wa-status`}>
        {busy === "share" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Share2 className="mr-1 h-3.5 w-3.5" />} Share to WhatsApp Status
      </Button>
      <Button size="sm" variant="outline" onClick={() => download()} disabled={!!busy} className="h-8" data-testid={`${testPrefix}-download`}>
        {busy === "dl" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} Download
      </Button>
      <Button size="sm" variant="outline" onClick={copy} className="h-8" data-testid={`${testPrefix}-copy-caption`}><Copy className="mr-1 h-3.5 w-3.5" /> Copy caption</Button>
      <Button size="sm" variant="ghost" asChild className="h-8 text-green-700"><a href="whatsapp://send" data-testid={`${testPrefix}-open-wa`}><MessageCircle className="mr-1 h-3.5 w-3.5" /> Open WhatsApp</a></Button>
    </div>
  );
};
